import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FiTag, FiPlus, FiEdit, FiTrash2, FiEye,
  FiEyeOff, FiGrid, FiList, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';

// ✅ Icônes style photo (FontAwesome solid) via helper
import { renderCategoryIcon } from '../../utils/categoryIcons';

const AdminCategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mountedRef = useRef(true);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const normalizeListFromResponse = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.docs)) return payload.docs;
    if (Array.isArray(payload.data?.data)) return payload.data.data;
    if (Array.isArray(payload.items)) return payload.items;
    return [];
  };

  const readTotalFromResponse = (axiosResponse) => {
    try {
      const res = axiosResponse?.data ?? {};
      const headers = axiosResponse?.headers || {};
      if (res && (typeof res.total === 'number' || typeof res.total === 'string')) return Number(res.total);
      if (res && res.meta && (typeof res.meta.total === 'number' || typeof res.meta.total === 'string')) return Number(res.meta.total);
      if (res && res.pagination && (typeof res.pagination.total === 'number' || typeof res.pagination.total === 'string')) return Number(res.pagination.total);
      if (res && res.totalItems) return Number(res.totalItems);
      if (headers && headers['x-total-count']) {
        const n = Number(headers['x-total-count']);
        if (!Number.isNaN(n)) return n;
      }
    } catch (e) {}
    return 0;
  };

  const fetchCategories = async (pageToFetch = 1, limitToFetch = 20) => {
    setLoading(true);
    try {
      const params = {
        page: pageToFetch,
        limit: limitToFetch,
        active: 'all'
      };

      const res = await API.get('/categories', { params });
      const payload = res?.data ?? {};

      let list = normalizeListFromResponse(payload);
      if (list.length === 0 && payload.data && Array.isArray(payload.data?.data)) {
        list = payload.data.data;
      }
      if (list.length === 0 && Array.isArray(payload)) list = payload;

      if (!mountedRef.current) return;
      setCategories(list || []);

      const totalItems =
        readTotalFromResponse(res) ||
        (payload?.meta?.total ?? payload?.pagination?.total ?? 0);

      if (totalItems) setTotal(Number(totalItems));
      else setTotal(list.length || 0);

      const limitFromServer =
        payload?.limit ??
        payload?.meta?.limit ??
        payload?.pagination?.limit ??
        payload?.itemsPerPage;

      if (limitFromServer) setPerPage(Number(limitFromServer));

      const pageFromServer =
        payload?.page ??
        payload?.currentPage ??
        payload?.meta?.currentPage ??
        payload?.pagination?.currentPage;

      if (pageFromServer) setPage(Number(pageFromServer));
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
      if (mountedRef.current) setCategories([]);
      setTotal(0);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(page, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // ✅ on stocke icon comme string (clé)
      const payloadToSend = {
        ...formData,
        icon: String(formData.icon || '').toLowerCase().trim()
      };

      if (editingCategory) {
        await API.put(`/categories/${editingCategory._id}`, payloadToSend);
        toast.success('Catégorie mise à jour');
      } else {
        await API.post('/categories', payloadToSend);
        toast.success('Catégorie créée');
      }

      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', icon: '', active: true });
      setPage(1);
      await fetchCategories(1, perPage);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      icon: category.icon || '',
      active: category.active !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (!categoryId) return;
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie?')) return;

    try {
      await API.delete(`/categories/${categoryId}`);
      toast.success('Catégorie supprimée');
      await fetchCategories(page, perPage);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (categoryId) => {
    try {
      await API.patch(`/categories/${categoryId}/toggle`);
      toast.success('Statut mis à jour');
      await fetchCategories(page, perPage);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const stats = {
    total: total || categories.length,
    active: categories.filter((c) => c.active !== false).length,
    inactive: categories.filter((c) => c.active === false).length
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || categories.length) / perPage)),
    [total, categories.length, perPage]
  );

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next === page) return;
    setPage(next);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Catégories</h1>
            <p className="text-gray-600 mt-1">Organisez et gérez les catégories de deals</p>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex bg-gray-100 rounded-lg p-1" role="tablist" aria-label="Mode d'affichage">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
                aria-pressed={viewMode === 'grid'}
                title="Vue grille"
              >
                <FiGrid />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
                aria-pressed={viewMode === 'list'}
                title="Vue liste"
              >
                <FiList />
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                setEditingCategory(null);
                setFormData({ name: '', description: '', icon: '', active: true });
                setShowModal(true);
              }}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              title="Nouvelle catégorie"
            >
              <FiPlus className="mr-2" />
              Nouvelle Catégorie
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Catégories</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FiTag className="text-blue-500 text-2xl" aria-hidden="true" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Actives</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <FiEye className="text-green-500 text-2xl" aria-hidden="true" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Inactives</p>
              <p className="text-2xl font-bold text-gray-600">{stats.inactive}</p>
            </div>
            <FiEyeOff className="text-gray-500 text-2xl" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Liste / Grille */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map((category) => (
            <div
              key={category._id || category.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  {/* ✅ icône style photo */}
                  <div className="bg-[#fde7ef] p-3 rounded-2xl">
                    {renderCategoryIcon(category.icon, 34, 'text-[#e11d7b]')}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleToggleActive(category._id)}
                    className={`p-1 rounded ${category.active !== false ? 'text-green-600' : 'text-gray-400'}`}
                    title={category.active !== false ? 'Actif' : 'Inactif'}
                    aria-pressed={category.active !== false}
                  >
                    {category.active !== false ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                  </button>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">{category.name}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {category.description || 'Aucune description'}
                </p>

                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-sm text-gray-500">
                    {category.dealsCount || 0} deals
                  </span>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(category)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                      title="Modifier"
                      aria-label={`Modifier ${category.name}`}
                    >
                      <FiEdit size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(category._id)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Supprimer"
                      aria-label={`Supprimer ${category.name}`}
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deals
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <tr
                  key={category._id || category.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="bg-[#fde7ef] p-2 rounded-xl mr-3">
                        {renderCategoryIcon(category.icon, 26, 'text-[#e11d7b]')}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{category.name}</span>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">{category.description || '-'}</span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{category.dealsCount || 0}</span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        category.active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {category.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(category._id)}
                        className={`${
                          category.active !== false
                            ? 'text-gray-600 hover:text-gray-900'
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={category.active !== false ? 'Désactiver' : 'Activer'}
                        aria-label={`${category.active !== false ? 'Désactiver' : 'Activer'} ${category.name}`}
                      >
                        {category.active !== false ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleEdit(category)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Modifier"
                        aria-label={`Modifier ${category.name}`}
                      >
                        <FiEdit size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(category._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                        aria-label={`Supprimer ${category.name}`}
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t bg-white mt-4 rounded-b">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1 || loading}
            className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
          >
            <FiChevronLeft /> <span>Préc</span>
          </button>

          <button
            type="button"
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages || loading}
            className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
          >
            <span>Suiv</span> <FiChevronRight />
          </button>

          <div className="ml-4 text-sm text-gray-600">
            Page <strong>{page}</strong> / <strong>{totalPages}</strong>
          </div>

          <div className="ml-4 text-sm text-gray-600">
            <span className="mr-2">Afficher</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              className="border px-2 py-1 rounded"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span className="ml-2">/ page</span>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          {total > 0 ? `Total: ${total} résultats` : `${categories.length} affichés`}
        </div>
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg max-w-md w-full">
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">
                  {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Ex: Restaurants"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Description de la catégorie"
                    />
                  </div>

                  {/* ✅ Select icônes style photo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Icône
                    </label>

                    <select
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Sélectionner une icône</option>

                      <optgroup label="Promotions / Général">
                        <option value="promotion">Promotion (Tag)</option>
                        <option value="reduction">Réduction (%)</option>
                        <option value="deals">Deals</option>
                        <option value="exclusif">Exclusif</option>
                        <option value="shopping">Shopping / Mode</option>
                        <option value="produits">Produits</option>
                        <option value="cadeaux">Cadeaux</option>
                      </optgroup>

                      <optgroup label="Restaurants">
                        <option value="restaurant">Restaurants & Cafés</option>
                        <option value="cafe">Café</option>
                        <option value="gastronomie">Gastronomie</option>
                      </optgroup>

                      <optgroup label="Santé / Beauté">
                        <option value="spa">Spa</option>
                        <option value="beaute">Beauté</option>
                        <option value="bienetre">Bien-être</option>
                        <option value="coiffure">Coiffure</option>
                        <option value="cosmetiques">Cosmétiques</option>
                        <option value="sante">Santé</option>
                        <option value="clinique">Clinique</option>
                        <option value="pharmacie">Pharmacie</option>
                      </optgroup>

                      <optgroup label="Sport / Fitness">
                        <option value="sport">Sport</option>
                        <option value="fitness">Fitness</option>
                        <option value="gym">Salle de sport</option>
                        <option value="running">Running</option>
                      </optgroup>

                      <optgroup label="Voyage">
                        <option value="voyage">Voyage</option>
                        <option value="tourisme">Tourisme</option>
                        <option value="hotel">Hôtel</option>
                        <option value="excursion">Excursion</option>
                      </optgroup>

                      <optgroup label="Immobilier">
                        <option value="immobilier">Immobilier</option>
                        <option value="logement">Logement</option>
                      </optgroup>

                      <optgroup label="Loisirs">
                        <option value="loisirs">Loisirs</option>
                        <option value="divertissement">Divertissement</option>
                        <option value="musique">Musique</option>
                        <option value="cinema">Cinéma</option>
                        <option value="jeux">Jeux</option>
                        <option value="photo">Photo</option>
                      </optgroup>

                      <optgroup label="Éducation">
                        <option value="education">Éducation</option>
                        <option value="formation">Formation</option>
                        <option value="cours">Cours</option>
                      </optgroup>

                      <optgroup label="Voiture / Transport">
                        <option value="voiture">Voiture</option>
                        <option value="location_voiture">Location voiture</option>
                        <option value="taxi">Taxi</option>
                        <option value="transport">Transport</option>
                      </optgroup>

                      <optgroup label="Services">
                        <option value="services">Services</option>
                        <option value="business">Business</option>
                        <option value="professionnel">Professionnel</option>
                        <option value="rendezvous">Rendez-vous</option>
                      </optgroup>

                      <optgroup label="Technologie">
                        <option value="technologie">Technologie</option>
                        <option value="mobile">Mobile</option>
                      </optgroup>

                      <optgroup label="Paiement / Sécurité / Support">
                        <option value="paiement">Paiement</option>
                        <option value="securite">Sécurité</option>
                        <option value="support">Support</option>
                      </optgroup>

                      <optgroup label="Autre">
                        <option value="autre">Autre</option>
                      </optgroup>
                    </select>

                    {formData.icon ? (
                      <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                        <span>Aperçu :</span>
                        <div className="bg-[#fde7ef] p-2 rounded-xl">
                          {renderCategoryIcon(formData.icon, 28, 'text-[#e11d7b]')}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="active"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                      Catégorie active
                    </label>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    setFormData({ name: '', description: '', icon: '', active: true });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {editingCategory
                    ? isSubmitting
                      ? 'Mise à jour...'
                      : 'Mettre à jour'
                    : isSubmitting
                    ? 'Création...'
                    : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategoriesManagement;
